/**
 * Utility functions for handling airport operating hours.
 */

export interface OperatingHoursInfo {
  startHour: number;
  endHour: number;
  operatingHoursString: string;
}

/**
 * Parse operating hours string from API (e.g., "07:00-20:00 UTC") into start/end hours.
 * Returns null if the string cannot be parsed.
 */
export function parseOperatingHours(operatingHoursString: string | undefined | null): OperatingHoursInfo | null {
  if (!operatingHoursString) return null;

  // Match pattern like "07:00-20:00 UTC" or "06:00-22:00 UTC"
  const match = operatingHoursString.match(/^(\d{2}):00-(\d{2}):00\s*UTC$/);
  if (!match) return null;

  const startHour = parseInt(match[1], 10);
  const endHour = parseInt(match[2], 10);

  if (isNaN(startHour) || isNaN(endHour)) return null;

  return {
    startHour,
    endHour,
    operatingHoursString
  };
}

/**
 * Check if a given time (in UTC) falls outside the operating hours.
 * Returns true if outside operating hours, false if within or if no operating hours defined.
 */
export function isOutsideOperatingHours(
  time: Date,
  operatingHours: OperatingHoursInfo | null
): boolean {
  if (!operatingHours) return false;

  const hour = time.getUTCHours();
  return hour < operatingHours.startHour || hour >= operatingHours.endHour;
}
